/**
 * Playbook review presentation.
 *
 * Renders the benchmark review a contract received against its review
 * framework: what each clause actually says, what BIB wants, how far apart they
 * are, the traps only visible across clauses, and what the contract never
 * addressed at all.
 */

import React, { useState } from 'react';
import {
  AlertOctagon,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  FileWarning,
  Link2,
  MessageSquareWarning,
  Target
} from 'lucide-react';
import {
  BenchmarkFinding,
  PlaybookClassification,
  PlaybookReview
} from '../../../../types/contract.types';

/**
 * Visual treatment per classification.
 *
 * Deliberately distinct from the generic risk palette: these are review
 * decisions from the framework, not the model's own severity guess, and a
 * reviewer should be able to tell at a glance which one they are looking at.
 */
const CLASSIFICATION_STYLES: Record<
  PlaybookClassification,
  { label: string; badge: string; border: string; Icon: React.ElementType }
> = {
  CRITICAL: {
    label: 'Critical',
    badge: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    border: 'border-red-300 dark:border-red-800',
    Icon: AlertOctagon
  },
  HIGH_RISK: {
    label: 'High risk',
    badge: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
    border: 'border-orange-300 dark:border-orange-800',
    Icon: AlertTriangle
  },
  NEGOTIATE: {
    label: 'Negotiate',
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    border: 'border-amber-300 dark:border-amber-800',
    Icon: MessageSquareWarning
  },
  ACCEPTABLE: {
    label: 'Acceptable',
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    border: 'border-emerald-300 dark:border-emerald-800',
    Icon: CheckCircle
  }
};

const ORDER: PlaybookClassification[] = ['CRITICAL', 'HIGH_RISK', 'NEGOTIATE', 'ACCEPTABLE'];

export const ClassificationBadge: React.FC<{
  classification: PlaybookClassification;
  className?: string;
}> = ({ classification, className = '' }) => {
  const style = CLASSIFICATION_STYLES[classification];
  const Icon = style.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${style.badge} ${className}`}
    >
      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
      {style.label}
    </span>
  );
};

/**
 * Present an extracted value in a form a reviewer can read.
 *
 * Booleans are the subtle case: `true` can mean "the protection exists" or "the
 * problematic right exists" depending on the parameter, so they are rendered as
 * plain Yes/No and left to be read against the benchmark shown beside them.
 */
const formatValue = (finding: BenchmarkFinding): string => {
  if (finding.absent || finding.actualValue === null) return 'Not addressed';
  if (typeof finding.actualValue === 'boolean') return finding.actualValue ? 'Yes' : 'No';
  if (typeof finding.actualValue === 'number') {
    return finding.unit ? `${finding.actualValue} ${finding.unit}` : String(finding.actualValue);
  }
  return String(finding.actualValue);
};

const FindingCard: React.FC<{ finding: BenchmarkFinding }> = ({ finding }) => {
  const [expanded, setExpanded] = useState(false);
  const style = CLASSIFICATION_STYLES[finding.classification];

  return (
    <div
      className={`border rounded-xl bg-white dark:bg-dark-surface ${style.border} overflow-hidden`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-gray-50 dark:hover:bg-dark-bg-secondary transition-colors"
      >
        {expanded ? (
          <ChevronDown className="w-4 h-4 mt-1 flex-shrink-0 text-gray-400" />
        ) : (
          <ChevronRight className="w-4 h-4 mt-1 flex-shrink-0 text-gray-400" />
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900 dark:text-white">{finding.label}</span>
            {finding.clauseRef && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Clause {finding.clauseRef}
              </span>
            )}
          </div>
          <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium text-gray-900 dark:text-gray-200">
              {formatValue(finding)}
            </span>
            <span className="mx-2 text-gray-300 dark:text-gray-600">vs</span>
            <span>{finding.preferredText}</span>
          </div>
        </div>

        <ClassificationBadge classification={finding.classification} className="flex-shrink-0" />
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 ml-7 space-y-3 text-sm border-t border-gray-100 dark:border-dark-border">
          {finding.evidence && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                Contract wording
              </div>
              <blockquote className="border-l-2 border-gray-300 dark:border-dark-border pl-3 text-gray-700 dark:text-gray-300 italic">
                {finding.evidence}
              </blockquote>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                Fallback position
              </div>
              <p className="text-gray-700 dark:text-gray-300">{finding.fallbackText}</p>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                Red flag
              </div>
              <p className="text-gray-700 dark:text-gray-300">{finding.redFlagText}</p>
            </div>
          </div>

          {finding.classification !== 'ACCEPTABLE' && (
            <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-300 mb-1">
                Challenge
              </div>
              <p className="text-gray-800 dark:text-gray-200">{finding.challenge}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface PlaybookReviewPanelProps {
  review: PlaybookReview;
  /** Hide the framework header when the parent already provides one. */
  compact?: boolean;
}

export const PlaybookReviewPanel: React.FC<PlaybookReviewPanelProps> = ({
  review,
  compact = false
}) => {
  const [showAcceptable, setShowAcceptable] = useState(false);

  const visibleFindings = showAcceptable
    ? review.findings
    : review.findings.filter(f => f.classification !== 'ACCEPTABLE');

  const acceptableCount = review.counts.ACCEPTABLE;

  return (
    <div className="space-y-6">
      {!compact && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Target className="w-5 h-5 text-primary-600 dark:text-accent-400" />
            {review.playbookName}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {review.perspective} &middot; Framework v{review.playbookVersion}
          </p>
        </div>
      )}

      {/* Counts across the risk ladder */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {ORDER.map(classification => {
          const style = CLASSIFICATION_STYLES[classification];
          const Icon = style.Icon;
          return (
            <div
              key={classification}
              className={`rounded-xl border p-3 ${style.border} bg-white dark:bg-dark-surface`}
            >
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {review.counts[classification]}
                </span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{style.label}</div>
            </div>
          );
        })}
      </div>

      {/* Cross-clause findings - traps invisible when reading clause by clause */}
      {review.crossClauseFindings.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-2">
            <Link2 className="w-4 h-4" />
            Cross-clause findings ({review.crossClauseFindings.length})
          </h4>
          <div className="space-y-3">
            {review.crossClauseFindings.map(cross => {
              const style = CLASSIFICATION_STYLES[cross.classification];
              return (
                <div
                  key={cross.ruleId}
                  className={`border rounded-xl p-4 ${style.border} bg-white dark:bg-dark-surface`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <h5 className="font-medium text-gray-900 dark:text-white">{cross.title}</h5>
                    <ClassificationBadge
                      classification={cross.classification}
                      className="flex-shrink-0"
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">{cross.finding}</p>
                  <div className="mt-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg p-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-300 mb-1">
                      Challenge
                    </div>
                    <p className="text-sm text-gray-800 dark:text-gray-200">{cross.challenge}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Parameter-by-parameter benchmarks */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Benchmarks ({visibleFindings.length})
          </h4>
          {acceptableCount > 0 && (
            <button
              onClick={() => setShowAcceptable(!showAcceptable)}
              className="text-xs text-primary-600 dark:text-accent-400 hover:underline"
            >
              {showAcceptable ? 'Hide' : 'Show'} {acceptableCount} acceptable
            </button>
          )}
        </div>

        {visibleFindings.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No benchmark deviations were identified.
          </p>
        ) : (
          <div className="space-y-2">
            {visibleFindings.map(finding => (
              <FindingCard key={finding.parameterId} finding={finding} />
            ))}
          </div>
        )}
      </div>

      {/* Coverage - what the contract never addressed */}
      <div>
        <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-2">
          <FileWarning className="w-4 h-4" />
          Coverage
        </h4>
        <div className="rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface p-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <span className="font-medium">{review.coverage.assessed}</span> of{' '}
            {review.coverage.totalParameters} framework parameters were assessed.{' '}
            {review.coverage.absent > 0 && (
              <>
                The contract is silent on{' '}
                <span className="font-medium">{review.coverage.absent}</span> of them.
              </>
            )}
          </p>

          {review.coverage.absentParameters.length > 0 && (
            <ul className="mt-3 space-y-2">
              {review.coverage.absentParameters.map(param => (
                <li key={param.parameterId} className="text-sm flex items-start gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                  <span>
                    <span className="font-medium text-gray-900 dark:text-white">{param.label}</span>
                    <span className="text-gray-500 dark:text-gray-400"> &mdash; {param.topic}</span>
                    <span className="block text-gray-600 dark:text-gray-400">{param.challenge}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Negotiation order */}
      {review.challengesByTier.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
            Negotiation priority
          </h4>
          <div className="space-y-4">
            {review.challengesByTier.map(group => (
              <div
                key={group.tier}
                className="rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface p-4"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 rounded-lg bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 text-xs font-bold flex items-center justify-center">
                    {group.tier}
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">{group.label}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    ({group.items.length})
                  </span>
                </div>
                <ul className="space-y-2">
                  {group.items.map(item => (
                    <li key={item.parameterId} className="text-sm flex items-start gap-3">
                      <ClassificationBadge
                        classification={item.classification}
                        className="flex-shrink-0 mt-0.5"
                      />
                      <span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {item.label}
                        </span>
                        <span className="block text-gray-600 dark:text-gray-400">
                          {item.challenge}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaybookReviewPanel;
